const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    console.log('SW registered:', reg.scope);
    return reg;
  } catch (err) {
    console.error('SW registration failed:', err);
    return null;
  }
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  const result = await Notification.requestPermission();
  return result;
}

// Subscribe to push notifications
export async function subscribeToPush(token) {
  const reg = await navigator.serviceWorker?.ready;
  if (!reg) return null;

  // Check if already subscribed
  let sub = await reg.pushManager.getSubscription();
  if (sub) return sub;

  // Subscribe (using VAPID-less approach for now — server-sent notifications)
  // For production, you'd generate VAPID keys and use them here
  try {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // applicationServerKey not needed for local/server-triggered notifications
    });
    return sub;
  } catch {
    return null;
  }
}

// Show a local notification (doesn't need push subscription)
export function showLocalNotification(title, body, options = {}) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.ready.then(reg => {
      reg.showNotification(title, {
        body,
        icon: '/assets/logo.png',
        badge: '/assets/logo.png',
        tag: options.tag || `lodgiflow-${Date.now()}`,
        data: options.data || {},
        vibrate: [200, 100, 200],
        ...options,
      });
    });
  } else {
    // Fallback to Notification API directly
    new Notification(title, { body, icon: '/assets/logo.png', ...options });
  }
}

// Poll for system alerts and show notifications
let alertPollingInterval = null;
let lastAlertCheck = null;

export function startAlertPolling(token, intervalMs = 60000) {
  if (alertPollingInterval) return;
  
  const check = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/system-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const statuses = data.statuses || [];
      const downSystems = statuses.filter(s => s.status === 'down');
      const degradedSystems = statuses.filter(s => s.status === 'degraded');

      if (lastAlertCheck !== null) {
        // Only notify on NEW down/degraded since last check
        for (const sys of downSystems) {
          const key = `${sys.property_id}-${sys.platform_type_id}-down`;
          if (!lastAlertCheck.has(key)) {
            showLocalNotification(
              `System DOWN: ${sys.platform_name || sys.platform_type_id}`,
              `${sys.property_name || sys.property_id} — ${sys.notes || 'System is down'}`,
              { tag: key, data: { url: '/app', type: 'system_down' } }
            );
          }
        }
        for (const sys of degradedSystems) {
          const key = `${sys.property_id}-${sys.platform_type_id}-degraded`;
          if (!lastAlertCheck.has(key)) {
            showLocalNotification(
              `System Degraded: ${sys.platform_name || sys.platform_type_id}`,
              `${sys.property_name || sys.property_id} — ${sys.notes || 'Performance impacted'}`,
              { tag: key, data: { url: '/app', type: 'system_degraded' } }
            );
          }
        }
      }

      lastAlertCheck = new Set([
        ...downSystems.map(s => `${s.property_id}-${s.platform_type_id}-down`),
        ...degradedSystems.map(s => `${s.property_id}-${s.platform_type_id}-degraded`),
      ]);
    } catch {
      // Ignore polling errors
    }
  };

  check(); // Initial check
  alertPollingInterval = setInterval(check, intervalMs);
}

export function stopAlertPolling() {
  if (alertPollingInterval) {
    clearInterval(alertPollingInterval);
    alertPollingInterval = null;
  }
}
